import React, { useState, useEffect } from 'react';
import { MOOD_OPTIONS } from '../../utils/moodTrackerUtils';
import { Card, Select, Badge, Spinner } from '../design-system';

const MoodComments = ({ moodHistory, loading, onMoodFilterChange, selectedMoodFilter = 'all' }) => {
  const [filteredComments, setFilteredComments] = useState([]);

  // Filter comments based on selected mood
  useEffect(() => {
    if (!moodHistory || !Array.isArray(moodHistory)) {
      setFilteredComments([]);
      return;
    }

    let filtered = moodHistory;
    
    if (selectedMoodFilter !== 'all') {
      filtered = moodHistory.filter(entry => 
        entry.mood === selectedMoodFilter && 
        entry.comment && 
        entry.comment.trim()
      );
    } else {
      filtered = moodHistory.filter(entry => 
        entry.comment && 
        entry.comment.trim()
      );
    }

    // Sort by date (newest first)
    filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    setFilteredComments(filtered);
  }, [moodHistory, selectedMoodFilter]);

  // Handle mood filter change
  const handleMoodFilterChange = (e) => {
    const newFilter = e.target.value;
    onMoodFilterChange(newFilter);
  };

  // Get mood option by ID
  const getMoodOption = (moodId) => {
    return MOOD_OPTIONS.find(mood => mood.id === moodId);
  };

  // Format date for display using proper timezone handling
  const formatDate = (entry) => {
    // Use createdAt timestamp if available, otherwise fall back to date string
    const timestamp = entry.createdAt || entry.timestamp || entry.date;
    return new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Format time for display
  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3">
            <Spinner size="large" />
            <span className="text-gray-600">Loading comments...</span>
          </div>
        </div>
      </Card>
    );
  }

  if (!moodHistory || moodHistory.length === 0) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">
          <div className="text-4xl mb-4">💭</div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            No Comments Yet
          </h3>
          <p className="text-gray-600">
            Start sharing your thoughts when recording your mood to see them here.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-800">
          Mood Comments
        </h3>
        <Select
          value={selectedMoodFilter}
          onChange={handleMoodFilterChange}
          className="w-40"
          options={[
            { value: 'all', label: 'All Moods' },
            ...MOOD_OPTIONS.map(mood => ({
              value: mood.id,
              label: `${mood.emoji} ${mood.label}`
            }))
          ]}
        />
      </div>

      {filteredComments.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-4xl mb-4">🔍</div>
          <h4 className="text-md font-semibold text-gray-800 mb-2">
            No Comments Found
          </h4>
          <p className="text-gray-600">
            {selectedMoodFilter === 'all' 
              ? 'No comments have been recorded yet.'
              : `No comments found for ${getMoodOption(selectedMoodFilter)?.label} mood.`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredComments.map((entry, index) => {
            const moodOption = getMoodOption(entry.mood);
            
            return (
              <div
                key={index}
                className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow duration-200"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded-full flex items-center justify-center text-lg"
                      style={{ backgroundColor: `${moodOption?.color}20` }}
                    >
                      {moodOption?.emoji}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge 
                          variant="outline"
                          style={{ 
                            borderColor: moodOption?.color,
                            color: moodOption?.color 
                          }}
                        >
                          {moodOption?.label}
                        </Badge>
                        <span className="text-sm text-gray-500">
                          {formatDate(entry)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400">
                        {formatTime(entry.timestamp)}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-gray-800 leading-relaxed">
                    {entry.comment}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary */}
      {filteredComments.length > 0 && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>
              Showing {filteredComments.length} comment{filteredComments.length !== 1 ? 's' : ''}
              {selectedMoodFilter !== 'all' && ` for ${getMoodOption(selectedMoodFilter)?.label} mood`}
            </span>
            <span>
              {selectedMoodFilter === 'all' 
                ? `${moodHistory.filter(e => e.comment && e.comment.trim()).length} total comments`
                : `${moodHistory.filter(e => e.mood === selectedMoodFilter && e.comment && e.comment.trim()).length} total for this mood`
              }
            </span>
          </div>
        </div>
      )}
    </Card>
  );
};

export default MoodComments;
